import { create } from 'zustand';
import { ChatSession, ChatMessage, ChatSessionDetail } from '../types';
import { chatAPI } from '../utils/api';
import toast from 'react-hot-toast';

interface PendingConfirmation {
  action: string;
  summary: string;
  data: Record<string, unknown>;
}

interface ChatState {
  sessions: ChatSession[];
  activeSessionId: string | null;
  messages: ChatMessage[];
  isOpen: boolean;
  isLoading: boolean;
  isStreaming: boolean;
  streamingContent: string;
  pendingConfirmation: PendingConfirmation | null;
  showSessionList: boolean;

  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  toggleSessionList: () => void;
  loadSessions: () => Promise<void>;
  selectSession: (id: string) => Promise<void>;
  newSession: () => void;
  sendMessage: (text: string, imageBase64?: string) => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
  renameSession: (id: string, title: string) => Promise<void>;
  confirmAction: (confirmed: boolean) => Promise<void>;
}

export const useChatStore = create<ChatState>()((set, get) => ({
  sessions: [],
  activeSessionId: null,
  messages: [],
  isOpen: false,
  isLoading: false,
  isStreaming: false,
  streamingContent: '',
  pendingConfirmation: null,
  showSessionList: false,

  toggleChat: () => set((state) => ({ isOpen: !state.isOpen })),
  openChat: () => set({ isOpen: true }),
  closeChat: () => set({ isOpen: false }),
  toggleSessionList: () => set((state) => ({ showSessionList: !state.showSessionList })),

  loadSessions: async () => {
    try {
      const response = await chatAPI.getSessions({ page: 1, limit: 50 });
      set({ sessions: response.data.sessions });
    } catch {
      toast.error('Errore nel caricamento delle sessioni');
    }
  },

  selectSession: async (id: string) => {
    set({ isLoading: true, activeSessionId: id, showSessionList: false });
    try {
      const response = await chatAPI.getSession(id);
      const detail: ChatSessionDetail = response.data;
      set({ messages: detail.messages, isLoading: false });
    } catch {
      toast.error('Errore nel caricamento della sessione');
      set({ isLoading: false });
    }
  },

  newSession: () => {
    set({
      activeSessionId: null,
      messages: [],
      streamingContent: '',
      pendingConfirmation: null,
      showSessionList: false,
    });
  },

  sendMessage: async (text: string, imageBase64?: string) => {
    const { activeSessionId, messages } = get();

    // Optimistically add user message to UI
    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      session_id: activeSessionId || '',
      role: 'user',
      content: text,
      tool_calls_json: null,
      tool_name: null,
      tokens_used: 0,
      latency_ms: null,
      created_at: new Date().toISOString(),
    };
    set({ messages: [...messages, userMessage], isStreaming: true, streamingContent: '' });

    try {
      let newSessionId = activeSessionId;

      for await (const rawEvent of chatAPI.sendMessage(activeSessionId, text, imageBase64)) {
        const state = get();
        // Backend sends {type, data: {...}} - unwrap the data payload
        const eventType = rawEvent.type;
        const d = rawEvent.data || {};

        switch (eventType) {
          case 'session': {
            newSessionId = d.id;
            set({ activeSessionId: d.id });
            break;
          }

          // Tool events are internal execution details, not conversation messages.
          case 'tool_start':
          case 'tool_result':
            break;

          case 'confirmation': {
            set({
              pendingConfirmation: {
                action: d.action || 'unknown',
                summary: d.summary || 'Confermare questa azione?',
                data: d,
              },
            });
            break;
          }

          case 'content': {
            set({ streamingContent: state.streamingContent + (d.text || '') });
            break;
          }

          case 'done': {
            const finalContent = get().streamingContent;
            if (finalContent) {
              const assistantMsg: ChatMessage = {
                id: d.message_id || `asst-${Date.now()}`,
                session_id: newSessionId || '',
                role: 'assistant',
                content: finalContent,
                tool_calls_json: null,
                tool_name: null,
                tokens_used: d.tokens || 0,
                latency_ms: null,
                created_at: new Date().toISOString(),
              };
              set((s) => ({ messages: [...s.messages, assistantMsg] }));
            }
            set({ streamingContent: '', isStreaming: false });
            get().loadSessions();
            break;
          }

          case 'error': {
            toast.error(d.message || 'Errore durante la conversazione');
            set({ isStreaming: false, streamingContent: '' });
            break;
          }
        }
      }
    } catch (error) {
      toast.error('Errore di connessione con l\'assistente');
      set({ isStreaming: false, streamingContent: '' });
    }
  },

  deleteSession: async (id: string) => {
    try {
      await chatAPI.deleteSession(id);
      set((state) => ({
        sessions: state.sessions.filter((s) => s.id !== id),
        ...(state.activeSessionId === id
          ? { activeSessionId: null, messages: [], pendingConfirmation: null }
          : {}),
      }));
      toast.success('Sessione eliminata');
    } catch {
      toast.error('Errore nell\'eliminazione della sessione');
    }
  },

  renameSession: async (id: string, title: string) => {
    try {
      const response = await chatAPI.updateSession(id, { title });
      const updated: ChatSession = response.data;
      set((state) => ({
        sessions: state.sessions.map((s) => (s.id === id ? updated : s)),
      }));
    } catch {
      toast.error('Errore nel rinominare la sessione');
    }
  },

  confirmAction: async (confirmed: boolean) => {
    const { activeSessionId, pendingConfirmation } = get();
    if (!activeSessionId || !pendingConfirmation) return;

    set({ pendingConfirmation: null });

    try {
      await chatAPI.confirmAction(activeSessionId, confirmed);

      // Add a message reflecting the user's choice
      const confirmMsg: ChatMessage = {
        id: `confirm-${Date.now()}`,
        session_id: activeSessionId,
        role: 'user',
        content: confirmed ? 'Confermato' : 'Annullato',
        tool_calls_json: null,
        tool_name: null,
        tokens_used: 0,
        latency_ms: null,
        created_at: new Date().toISOString(),
      };
      set((state) => ({ messages: [...state.messages, confirmMsg] }));

      if (!confirmed) {
        toast('Azione annullata');
      }
    } catch {
      toast.error('Errore nella conferma dell\'azione');
    }
  },
}));
